import { Suspense, lazy } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import ActivityLogByFormAnswerGroupId from '@/pages/ActivityLog/ActivityLogByFormAnswerGroupId';
import AppointmentView from '@/pages/AppointmentView';
import AppSettings from '@/pages/AppSettings';
import BillingClaim from '@/pages/BillingClaim';
import BillingDashboard from '@/pages/BillingDashboard';
import BillingItemsList from '@/pages/BillingItemsList';
import BillingApp from '@/pages/BillingReportResults/BillingApp';
import BillingReportResultsIndex from '@/pages/BillingReportResults/BillingReportResultsIndex';
import BillingReports from '@/pages/BillingReports/BillingReports';
import Blueprints from '@/pages/Blueprints';
import Calendar from '@/pages/Calendar';
import PersonGroupMemberships from '@/pages/Community';
import ContractDetail from '@/pages/ContractDetail';
import Contracts from '@/pages/Contracts';
import Credentialing from '@/pages/Credentialing/Credentialing';
import Dashboard from '@/pages/Dashboard';
import EditBillingReport from '@/pages/EditBillingReport';
import EditReferral from '@/pages/EditReferral';
import EditRemit from '@/pages/EditRemit/EditRemit';
import EditRemitDetails from '@/pages/EditRemitDetails';
import EncounterDetails from '@/pages/EncounterDetails';
import EncounterDetailsBilling from '@/pages/EncounterDetails/EncounterDetailsBilling';
import EncounterDetailsHome from '@/pages/EncounterDetails/EncounterDetailsHome';
import EncounterDetailsNewDrugScreen from '@/pages/EncounterDetails/EncounterDetailsNewDrugScreen';
import EncounterList from '@/pages/EncounterList';
import EncounterMarkNonBillable from '@/pages/EncounterMarkNonBillable';
import Encounters from '@/pages/Encounters';
import EngDashboard from '@/pages/EngDashboard';
import FormBuilder from '@/pages/FormBuilder';
import InsurancePolicyTimeline from '@/pages/InsurancePolicyTimeline/InsurancePolicyTimeline';
import InsuranceVerificationsList from '@/pages/InsuranceVerificationsList';
import InsuranceVOBHistory from '@/pages/InsuranceVOBHistory';
import ListRemits from '@/pages/ListRemits';
import ListTreatabilityMatches from '@/pages/ListTreatabilityMatches';
import MyProfile from '@/pages/MyProfile';
import MyWorkspace from '@/pages/MyWorkspace';
import NewBillingReport from '@/pages/NewBillingReport';
import NewInsuranceFromDiscovered from '@/pages/NewInsuranceFromDiscovered';
import NewNote from '@/pages/NewNote';
import NewReferral from '@/pages/NewReferral';
import NewRemit from '@/pages/NewRemit/NewRemit';
import NewROI from '@/pages/NewROI';
import NewTask from '@/pages/NewTask';
import OrganizationEdit from '@/pages/OrganizationEdit';
import Organizations from '@/pages/Organizations';
import OrganizationView from '@/pages/OrganizationView';
import People from '@/pages/People';
import Person from '@/pages/Person';
import PersonAiProfile from '@/pages/PersonAiProfile';
import PersonAppointments from '@/pages/PersonAppointments';
import PersonAuthentication from '@/pages/PersonAuthentication';
import PersonAvailability from '@/pages/PersonAvailability';
import PersonDrugScreen from '@/pages/PersonDrugScreen';
import PersonDrugScreens from '@/pages/PersonDrugScreens';
import PersonInsurance from '@/pages/PersonInsurance';
import PersonMessaging from '@/pages/PersonMessaging';
import PersonMetrics from '@/pages/PersonMetrics';
import PersonMissions from '@/pages/PersonMissions';
import PersonNew from '@/pages/PersonNew';
import PersonNewDrugScreen from '@/pages/PersonNewDrugScreen';
import PersonNoteList from '@/pages/PersonNoteList';
import PersonProgramQualifications from '@/pages/PersonProgramQualifications';
import PersonRequestedFormCompletions from '@/pages/PersonRequestedFormCompletions';
import PersonTaskList from '@/pages/PersonTaskList';
import Profile from '@/pages/Profile';
import RewardCardAccountView from '@/pages/RewardCardAccountView';
import RewardCardList from '@/pages/RewardCardList';
import RootHandler from '@/pages/RootHandler';
import Schedules from '@/pages/Schedules';
import Scheduling from '@/pages/Scheduling';
import SubstanceTracker from '@/pages/SubstanceTracker';
import Task from '@/pages/Task';
import Tasks from '@/pages/Tasks';
import TaskView from '@/pages/TaskView';
import Timeline from '@/pages/Timeline';
import TransactionList from '@/pages/TransactionList';
import Treatability from '@/pages/Treatability';
import TreatmentGroups from '@/pages/TreatmentGroups';
import NotAuthorized from '@/ui/components/NotAuthorized';
import RedirectWithError from '@/ui/components/RedirectWithError';
import Spinner from '@/ui/components/Spinner';

import Achievement from './pages/Achievements';
import AutoReverifyInsurance from './pages/AutoReverifyInsurance';
import { Booking } from './pages/Booking';
import PersonAchievement from './pages/PersonAchievement';
import PersonChallenge from './pages/PersonChallenge';
import PersonMedications from './pages/PersonMedications';

const MyCircles = lazy(() => import('@/pages/MyCircles'));

const insufficientPermissionsMessage = "You don't have permission for this action";

interface Props {
  enableAiProfile: boolean;
  enableBilling: boolean;
  enableEncounters: boolean;
  enableRewardCards: boolean;
  enableImpersonate?: boolean;
  enableMfaReset?: boolean;
  enableTimeline: boolean;
  enablePersonAvailability: boolean;
  enableVobList?: boolean;
  enableBillingTimeline?: boolean;
  enableEng?: boolean;
}

const buildAgentRoutes = (
  prefix: string,
  {
    enableAiProfile,
    enableBilling,
    enableEncounters,
    enableRewardCards,
    enableImpersonate,
    enableMfaReset,
    enableTimeline,
    enablePersonAvailability,
    enableVobList,
    enableBillingTimeline,
    enableEng
  }: Props
) => {
  return (
    <Route path={prefix} element={<RootHandler />}>
      <Route path="" element={<MyWorkspace />}>
        <Route index element={<People />} />
        <Route path="people/new" element={<PersonNew />} />
        <Route path="tasks" element={<Tasks showAddTask={false} />} />
      </Route>
      <Route path="settings" element={<AppSettings />} />
      <Route path="my-profile" element={<MyProfile />} />
      <Route path="credentialing" element={<Credentialing />} />
      <Route
        path="my-circles/:circleId?"
        element={
          <Suspense fallback={<Spinner />}>
            <MyCircles />
          </Suspense>
        }
      />
      <Route path="referrals/new" element={<NewReferral />} />
      <Route path="blueprints" element={<Blueprints />} />
      <Route path="blueprints/:blueprintId/form" element={<FormBuilder />} />
      <Route path="referrals/:referralId/edit" element={<EditReferral />} />
      <Route path="organizations" element={<Organizations />} />
      <Route path="organizations/:organizationId" element={<OrganizationView />} />
      <Route path="organizations/:organizationId/edit" element={<OrganizationEdit />} />
      <Route path="contracts" element={<Contracts />} />
      <Route path="contracts/:contractId" element={<ContractDetail />} />

      <Route path="people/:personId/*" element={<Person />}>
        <Route path="*" element={<Profile />} />
        <Route path="profile" element={<Profile />} />
        {enableTimeline && <Route path="timeline" element={<Timeline />} />}
        <Route path="community" element={<PersonGroupMemberships />} />
        <Route path="treatability" element={<Treatability />} />
        <Route path="appointments" element={<PersonAppointments />} />
        <Route path="appointments/:appointmentId" element={<AppointmentView />} />
        <Route path="challenges" element={<PersonChallenge />} />
        <Route path="missions" element={<PersonMissions />} />
        <Route path="metrics" element={<PersonMetrics />} />
        <Route path="messaging" element={<PersonMessaging />} />
        <Route path="substance-tracker" element={<SubstanceTracker />} />
        <Route path="drug-screens" element={<PersonDrugScreens />} />
        <Route path="drug-screens/new" element={<PersonNewDrugScreen />} />
        <Route path="drug-screens/:drugScreenId" element={<PersonDrugScreen />} />
        {enableAiProfile && <Route path="ai-profile" element={<PersonAiProfile />} />}
        <Route
          path="availability"
          element={enablePersonAvailability ? <PersonAvailability /> : <NotAuthorized />}
        />
        <Route path="notes" element={<PersonNoteList />} />
        <Route path="insurance" element={<PersonInsurance />} />
        <Route
          path="billing/timeline"
          element={enableBillingTimeline ? <InsurancePolicyTimeline /> : <NotAuthorized />}
        />
        <Route path="insurance/:insuranceId/vob-history" element={<InsuranceVOBHistory />} />
        <Route
          path="insurance/:insuranceId/new-from-discovered"
          element={<NewInsuranceFromDiscovered />}
        />
        <Route path="insurance/:insuranceId/auto-reverify" element={<AutoReverifyInsurance />} />
        <Route path="documents" element={<PersonRequestedFormCompletions />} />
        <Route path="documents/new-roi" element={<NewROI />} />

        <Route path="achievements" element={<Achievement />} />
        <Route path="person-achievements" element={<PersonAchievement />} />
        <Route path="scheduling" element={<Scheduling />} />
        <Route path="program-qualifications" element={<PersonProgramQualifications />} />
        <Route path="medications" element={<PersonMedications />} />
        <Route path="booking" element={<Booking />} />

        {enableRewardCards && (
          <>
            <Route path="reward-cards" element={<RewardCardList />} />
            <Route
              path="reward-card-account/:rewardCardAccountId"
              element={<RewardCardAccountView />}
            />
            <Route path="transactions" element={<TransactionList />} />
          </>
        )}

        <Route path="tasks" element={<PersonTaskList />} />
        <Route path="tasks/new" element={<NewTask />} />
        <Route path="tasks/:itemId" element={<TaskView />} />
        <Route path="tasks/:itemId/edit" element={<Task />} />
        {(enableImpersonate || enableMfaReset) && (
          <Route
            path="authentication"
            element={
              <PersonAuthentication
                canImpersonate={!!enableImpersonate}
                canResetMfa={!!enableMfaReset}
              />
            }
          />
        )}
        {/* Impersonation moved under Authentication; keeps old links working. */}
        {enableImpersonate && (
          <Route path="impersonate" element={<Navigate to="../authentication" replace />} />
        )}
      </Route>
      <Route
        path="activity-logs/by-form-answer-group-id/:formAnswerGroupHealthieId/*"
        element={<ActivityLogByFormAnswerGroupId />}
      />

      {enableEncounters && (
        <>
          <Route
            path="encounters/:encounterType/by_healthie_id/:encounterHealthieId/*"
            element={<Encounters />}>
            <Route path="*" element={<EncounterList />} />
          </Route>
          <Route
            path="encounters/:encounterType/by_id/:encounterId/*"
            element={<EncounterDetails />}>
            <Route path="*" element={<EncounterDetailsHome />} />
            <Route path="drug-screens/new" element={<EncounterDetailsNewDrugScreen />} />
            <Route path="billing" element={<EncounterDetailsBilling />} />
            <Route path="mark-non-billable" element={<EncounterMarkNonBillable />} />
          </Route>
        </>
      )}

      <Route
        path="billing"
        element={enableBilling ? <BillingDashboard /> : <NotAuthorized />}></Route>
      <Route
        path="billing/reports"
        element={enableBilling ? <BillingReports /> : <NotAuthorized />}></Route>
      <Route
        path="billing/reports/new"
        element={enableBilling ? <NewBillingReport /> : <NotAuthorized />}></Route>
      <Route
        path="billing/reports/:billingReportId/edit"
        element={enableBilling ? <EditBillingReport /> : <NotAuthorized />}></Route>
      <Route
        path="billing/reports/:billingReportId/results"
        element={enableBilling ? <BillingReportResultsIndex /> : <NotAuthorized />}></Route>
      <Route
        path="billing/reports/:billingReportId/results/:reportResultId"
        element={enableBilling ? <BillingApp /> : <NotAuthorized />}></Route>
      <Route
        path="billing/items"
        element={enableBilling ? <BillingItemsList /> : <NotAuthorized />}></Route>
      <Route
        path="billing/claims/:by/:id"
        element={enableBilling ? <BillingClaim /> : <NotAuthorized />}></Route>
      <Route
        path="billing/claims/:id"
        element={enableBilling ? <BillingClaim /> : <NotAuthorized />}></Route>
      <Route
        path="billing/remits"
        element={enableBilling ? <ListRemits /> : <NotAuthorized />}></Route>
      <Route
        path="billing/remit/new"
        element={enableBilling ? <NewRemit /> : <NotAuthorized />}></Route>
      <Route
        path="billing/remit/:id/edit/details"
        element={enableBilling ? <EditRemitDetails /> : <NotAuthorized />}></Route>
      <Route
        path="billing/remit/:id/edit"
        element={enableBilling ? <EditRemit /> : <NotAuthorized />}></Route>

      <Route path="appointments/:appointmentId" element={<AppointmentView />} />

      <Route path="treatability-matches" element={<ListTreatabilityMatches />} />

      <Route path="treatment-groups" element={<TreatmentGroups />} />

      <Route path="schedules" element={<Schedules />} />

      <Route path="calendar" element={<Calendar />} />

      <Route
        path="vob/list"
        element={enableVobList ? <InsuranceVerificationsList /> : <NotAuthorized />}
      />

      <Route path="notes/new" element={<NewNote />} />

      <Route path="dashboard" element={<Dashboard />}></Route>
      <Route
        path="eng/dashboard"
        element={
          enableEng ? (
            <EngDashboard />
          ) : (
            <RedirectWithError to="/" message={insufficientPermissionsMessage} />
          )
        }
      />
      <Route path="tasks/new" element={<NewTask />} />
      <Route path="tasks/:itemId" element={<TaskView />} />
      <Route path="tasks/:itemId/edit" element={<Task />} />
    </Route>
  );
};

const DugoutRouter = ({
  enableAiProfile,
  enableBilling,
  enableEncounters,
  enableRewardCards,
  enableImpersonate,
  enableMfaReset,
  enableTimeline,
  enablePersonAvailability,
  enableVobList,
  enableBillingTimeline,
  enableEng
}: Props) => {
  return (
    <BrowserRouter>
      <Routes>
        {buildAgentRoutes('/', {
          enableAiProfile,
          enableBilling,
          enableEncounters,
          enableRewardCards,
          enableImpersonate,
          enableMfaReset,
          enableTimeline,
          enablePersonAvailability,
          enableVobList,
          enableBillingTimeline,
          enableEng
        })}
        {buildAgentRoutes('/agent/', {
          enableAiProfile,
          enableBilling,
          enableEncounters,
          enableRewardCards,
          enableImpersonate,
          enableMfaReset,
          enableTimeline,
          enablePersonAvailability,
          enableVobList,
          enableBillingTimeline,
          enableEng
        })}
      </Routes>
    </BrowserRouter>
  );
};

export default DugoutRouter;
